import { useRouteError, isRouteErrorResponse } from 'react-router';

/**
 * Root Error Boundary for React Router
 * Handles routing errors and provides fallback UI
 */
export function RootErrorBoundary() {
  const error = useRouteError();
  const showDetails = import.meta.env.DEV;
  
  let errorMessage = 'An unexpected error occurred';
  let errorDetails = '';

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || `Error ${error.status}`;
    const d = error.data;
    errorDetails = typeof d?.message === 'string' ? d.message : (typeof d === 'string' ? d : '');
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = showDetails ? error.stack || '' : '';
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (error != null && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    errorMessage = (error as { message: string }).message;
  }

  // Friendly message for React "Objects are not valid as a React child" (e.g. from rendering user object)
  if (typeof errorMessage === 'string' && errorMessage.includes('Objects are not valid as a React child')) {
    errorMessage = 'A display error occurred. Try signing out and signing in again.';
  }

  console.error('🚨 RootErrorBoundary caught error:', error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-neutral-900 mb-2">
          Oops! Something went wrong
        </h1>
        <p className="text-neutral-600 mb-4">{errorMessage}</p>
        {showDetails && errorDetails && (
          <details className="text-left text-sm text-neutral-500 mb-6">
            <summary className="cursor-pointer font-medium mb-2">Technical Details</summary>
            <pre className="overflow-auto p-3 bg-neutral-100 rounded text-xs">
              {errorDetails}
            </pre>
          </details>
        )}
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go Home
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-neutral-200 text-neutral-900 rounded-lg hover:bg-neutral-300 transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
